import React from "react";

import { resolveLinkAttrs } from "~/lib/shared/link-rel";

export type SafeLinkProps = Omit<
  React.AnchorHTMLAttributes<HTMLAnchorElement>,
  "href"
> & {
  /** Destination URL. Required — anchors without an href bypass the policy. */
  href: string;
  /**
   * Mark the destination as user-generated (coach-supplied). External hrefs
   * then render with `rel="ugc"`. Editorial links (the default) stay dofollow.
   */
  ugc?: boolean;
};

/**
 * SafeLink — the single anchor component for raw `<a>` rendering across the
 * platform. Applies the link attribute policy (see `link-rel.ts`) automatically:
 *
 *   - Owned-domain / relative hrefs render as a plain anchor (dofollow, no
 *     forced target, caller rel/target win).
 *   - External hrefs default to `target="_blank"` with `noopener noreferrer`,
 *     and gain `rel="ugc"` when `ugc` is set. Caller rel tokens are preserved.
 *
 * Prefer this over a bare `<a>` whenever the href can come from data
 * (CCO/CTP/coach profile fields, markdown bodies, citations, etc.).
 */
export const SafeLink = React.forwardRef<HTMLAnchorElement, SafeLinkProps>(
  function SafeLink({ href, rel, target, ugc, children, ...rest }, ref) {
    const attrs = resolveLinkAttrs(href, { ugc, rel, target });
    return (
      <a {...rest} ref={ref} href={href} rel={attrs.rel} target={attrs.target}>
        {children}
      </a>
    );
  },
);
